// Angular
import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
// RxJS
import { Observable, Subject, BehaviorSubject, throwError } from 'rxjs';
// CRUD
import { HttpUtilsService, QueryParamsModel, QueryResultsModel } from '../../../core/_base/crud';
// Models
import { Drawing } from '../../../core/auth/_models/drawing.model';
import { PreBuildTier } from '../../../core/auth/_models/prebuildtier.model';
import { catchError, map, timeout } from 'rxjs/operators';


const API_PROJECTS_URL = '/api/drawing';
const API_PREBUILD_URL = '/api/prebuild';
const API_CONVERSION_URL = '/api/convert-svg-to-dxf';
@Injectable()
export class DrawingService {
  constructor(private http: HttpClient, private httpUtils: HttpUtilsService) {
  }

  private subject = new Subject<any>();
  public toolSubject = new Subject<any>();
  private toolCLick = new Subject<any>();
  sharedData = new BehaviorSubject<any>(null);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable();
  private uuidSource = new BehaviorSubject<string>('');
  currentUuid = this.uuidSource.asObservable();
  private busBarViewSubject = new Subject<any>();
  busBarViewUpdates$: Observable<any> = this.busBarViewSubject.asObservable();
  private selectedCoverSubject = new BehaviorSubject<{ selectedCover: string | null, selectedSideCover: string | null }>({
    selectedCover: null,
    selectedSideCover: null
  });

  selectedCover$ = this.selectedCoverSubject.asObservable();

  showLoading() {
    this.loadingSubject.next(true);
  }

  hideLoading() {
    this.loadingSubject.next(false);
  }

  sendMessage(message: boolean) {
    this.subject.next(message);
  }

  clearMessages() {
    this.subject.next();
  }

  getMessage(): Observable<any> {
    return this.subject.asObservable();
  }


  sendToolCLick(message: string) {
    this.toolCLick.next(message);
  }

  clearToolClick() {
    this.toolCLick.next();
  }

  getToolClick(): Observable<any> {
    return this.toolCLick.asObservable();
  }

  // CREATE =>  POST: add a new customer to the server
  createDiagram(diagram: Drawing): Observable<Drawing> {
    // Note: Add headers if needed (tokens/bearer)
    const httpHeaders = this.httpUtils.getHTTPHeaders();
    return this.http.post<Drawing>(API_PROJECTS_URL, diagram, { headers: httpHeaders, responseType: 'text' as 'json' });
  }
  createPreBuildTierDiagram(diagram: PreBuildTier): Observable<PreBuildTier> {
    // Note: Add headers if needed (tokens/bearer)
    const httpHeaders = this.httpUtils.getHTTPHeaders();
    return this.http.post<PreBuildTier>(API_PREBUILD_URL, diagram, { headers: httpHeaders, responseType: 'text' as 'json' });
  }
  getPreBuildTierDiagram(userId: string): Observable<PreBuildTier[]> {
    return this.http.get<PreBuildTier[]>(API_PREBUILD_URL + `/${userId}`);
  }
  getPreBuildTierDiagramJustCreated(userId: string, PreBuildTierName: string): Observable<PreBuildTier[]> {
    return this.http.get<PreBuildTier[]>(API_PREBUILD_URL + `/${userId}` + `/${PreBuildTierName}`);
  }
  updatePreBuildTierDiagram(userId: string, PreBuildTierName: string, data: PreBuildTier): Observable<PreBuildTier> {
    return this.http.put<PreBuildTier>(`${API_PREBUILD_URL}/${userId}/${PreBuildTierName}`, data);
  }
  deleteItem(tierName: string, userId: string): Observable<any> {
    const url = `${API_PREBUILD_URL}/items/${tierName}/${userId}`;
    return this.http.delete(url);
  }

  // READ
  getAll(): Observable<Drawing[]> {
    return this.http.get<Drawing[]>(API_PROJECTS_URL);
  }

  getDiagramById(diagramId: string): Observable<Drawing> {
    return this.http.get<Drawing>(API_PROJECTS_URL + `/${diagramId}`);
  }

  getDiagramCalculation(userId: any, diagramId: string): Observable<Drawing> {
    return this.http.get<Drawing>(API_PROJECTS_URL + '/calculation' + `/${userId}/${diagramId}`);
  }

  // Method from server should return QueryResultsModel(items: any[], totalsCount: number)
  // items => filtered/sorted result
  // Server should return filtered/sorted result
  finddiagram(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    // Note: Add headers if needed (tokens/bearer)
    const httpHeaders = this.httpUtils.getHTTPHeaders();
    const httpParams = this.httpUtils.getFindHTTPParams(queryParams);

    const url = API_PROJECTS_URL + '/find';
    return this.http.get<QueryResultsModel>(url, {
      headers: httpHeaders,
      params: httpParams,
      responseType: 'text' as 'json'
    });
  }

  // UPDATE => PUT: update the customer on the server
  updateDiagram(project: Drawing): Observable<any> {
    const httpHeader = this.httpUtils.getHTTPHeaders();
    return this.http.put(API_PROJECTS_URL + `/${project.uuid}`, JSON.stringify(project), { headers: httpHeader, responseType: 'text' as 'json' });
  }

  // UPDATE Status
  updateStatusForProject(customers: Drawing[], status: number): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders();
    const body = {
      customersForUpdate: customers,
      newStatus: status
    };
    const url = API_PROJECTS_URL + '/updateStatus';
    return this.http.put(url, body, { headers: httpHeaders, responseType: 'text' as 'json' });
  }

  // DELETE => delete the customer from the server
  deleteDiagramFromSuperAdmin(projectId: string): Observable<Drawing> {
    const url = `${API_PROJECTS_URL}/${projectId}`;
    return this.http.delete<Drawing>(url);
  }


  // DELETE => delete the customer from the server
  deleteDiagram(drawingId: string): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders();
    const url = `${API_PROJECTS_URL}/deleteDrawing/${drawingId}`;
    return this.http.put(url, { headers: httpHeaders, responseType: 'text' as 'json' });
  }

  deleteProjects(ids: string[] = []): Observable<any> {
    const url = API_PROJECTS_URL + '/deleteProjects';
    const httpHeaders = this.httpUtils.getHTTPHeaders();
    const body = { projectIdsForDelete: ids };
    return this.http.put<QueryResultsModel>(url, body, { headers: httpHeaders, responseType: 'text' as 'json' });
  }
  setSharedData(data: any) {
    this.sharedData.next(data); // Emit the new data
  }
  getSharedData() {
    return this.sharedData.asObservable();
  }
  uuidShare(data: string) {
    this.uuidSource.next(data);
  }
  notifyBusBarViewUpdate(nodeData: any): void {
    this.busBarViewSubject.next(nodeData);
  }


  setSelectedCover(cover: { selectedCover: string | null, selectedSideCover: string | null }) {
    this.selectedCoverSubject.next(cover);
  }

  sendToBackendForConversion(svgContent: string): Observable<Blob> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(API_CONVERSION_URL, { svg: svgContent }, { headers, responseType: 'blob' })
      .pipe(
        timeout(120000),
        catchError(async (err) => {
          // Error body comes as a Blob because responseType is 'blob' -> read it as text
          let message = err.message || 'Conversion failed';
          if (err.error instanceof Blob) {
            try {
              const text = await new Response(err.error).text();
              try {
                const json = JSON.parse(text);
                message = json.details || json.error || text;
              } catch (e) {
                message = text;
              }
            } catch (e) { }
          }
          console.error('SVG to DXF conversion error:', message);
          throw new Error(message);
        })
      );
  }



}
